import dayjs from 'dayjs';
import {useCallback, useMemo} from 'react';

import {expr, type OptionalGetter, use} from '../../mobx-toolbox';
import type {Day, Millisecond} from '../../Time';
import add from '../../util/add';
import clamp from '../../util/clamp';
import type {DailySlideProps} from './DailySlideProps';

export type DailySlide = {
  key: `${Day}`;
  props: DailySlideProps;
};

export default function useDailySlides(
  now: Millisecond,
  getShift: OptionalGetter<Day>,
  setShift: (_: Day) => void,
): readonly [
  slides: readonly DailySlide[],
  getIndex: () => number,
  setIndex: (_: number) => void,
] {
  const shift = use(getShift);
  const slides = useMemo(
    () => [
      createSlide(now, shift, -1),
      createSlide(now, shift, 0),
      createSlide(now, shift, 1),
    ],
    [now, shift],
  );

  const getIndex = useCallback(
    () =>
      expr(() => {
        const shift = use(getShift);
        const index = slides.findIndex(_ => _.props.shift === shift);
        return index === -1 ? DEFAULT_INDEX : index;
      }),
    [getShift, slides],
  );

  const setIndex = useCallback(
    (_: number) => {
      const normalized = Math.round(clamp(_, slides.length - 1));
      setShift(slides[normalized].props.shift);
    },
    [setShift, slides],
  );

  return [slides, getIndex, setIndex];
}

function createSlide(now: Millisecond, shift: Day, delta: number): DailySlide {
  const nextShift = add(shift, delta);
  const $shiftedTime = dayjs(now).startOf('day').add(nextShift, 'day');
  const shiftedTime = $shiftedTime.valueOf() as Millisecond;
  return {
    key: `${nextShift}`,
    props: {now: shiftedTime, shift: nextShift},
  };
}

const DEFAULT_INDEX = 1; // the center between 0th and 2nd slide
